import WebApp from '@twa-dev/sdk';

import { sendAnalyticsEvent } from '@/shared/api/platform';
import { base64Utils } from '@/shared/helpers/base64-utils';
import { getDeviceInfo } from '@/shared/helpers/environment/get-device-info';
import { getOpeningMethod } from '@/shared/helpers/environment/get-opening-method';
import { type UseWsReturnType } from '@/shared/hooks/use-ws';
import { MessageAction, MessageType } from '@/shared/hooks/use-ws/types';
import { type StartAppArgs } from '@/shared/stores/app-store/types';

import { BaseLogger } from '../logger/BaseLogger';
import { LogDevMessageKinds } from '../logger/types';
import { type EventEnrichment, type PlatformEvent } from './types';

interface EventPredefinedValues {
  user_properties: Record<string, any>;
  event_properties: Record<string, any>;
}

interface InitAnalyticsOptions {
  startAppArgs: StartAppArgs;
  startAppRaw: string | undefined;
  ws: UseWsReturnType;
  pdTma: string | null;
}

export enum PROTOCOL {
  HTTP = 'http',
  WEBSOCKET = 'websocket',
}

export class AnalyticsService {
  private static enrichEventData: EventEnrichment = {} as EventEnrichment;
  private static log = BaseLogger.makeLogDevMessage<PlatformEvent, string>(
    LogDevMessageKinds.ws,
    (event) => event.type,
    ['EVENT', (event) => event]
  );
  private static predefinedValues: EventPredefinedValues = {} as EventPredefinedValues;
  private static ws: InitAnalyticsOptions['ws'];

  public static init({ startAppArgs, startAppRaw, ws, pdTma }: InitAnalyticsOptions) {
    const telegram_id = WebApp?.initDataUnsafe?.user?.id;
    const tg_api_version = WebApp?.version;
    const is_premium = !!WebApp?.initDataUnsafe?.user?.is_premium;
    const tg_platform = WebApp?.platform;
    const deviceInfo = getDeviceInfo(WebApp?.platform);
    const openingMethod = getOpeningMethod();
    const decodedStartAppRaw = startAppRaw ? base64Utils.decode(startAppRaw) : undefined;

    this.predefinedValues = {
      user_properties: {
        telegram_id,
        tg_platform,
        tg_api_version,
        is_premium,
        ...deviceInfo,
      },
      event_properties: {
        event_source: 'wrapper',
        opening_method: openingMethod,
        ...(this.shouldSendStartapp(decodedStartAppRaw) && {
          startapp: decodedStartAppRaw,
        }),
        source: startAppArgs?.src ?? 'unknown',
        ...(pdTma && { TMAsrc: pdTma }),
      },
    };

    this.ws = ws;
  }

  public static send(
    event: PlatformEvent,
    type: MessageType = MessageType.ANALYTICS,
    protocol: PROTOCOL = PROTOCOL.WEBSOCKET
  ): void {
    if (!event) {
      return;
    }

    const enriched = this.enrichEvent(event);

    this.log(enriched);

    if (import.meta.env.DEV) return;

    if (protocol === PROTOCOL.WEBSOCKET) {
      this.sendViaWebSocket(enriched, type);
    } else {
      this.sendViaHttp(enriched);
    }
  }

  private static enrichEvent(event: PlatformEvent): PlatformEvent {
    const enrichRecord: Record<string, any> = {};

    const evtName = event.event_properties?.name;
    if (this.enrichEventData?.name === evtName) {
      const field = this.enrichEventData.field;
      const value = this.enrichEventData.value;
      enrichRecord[field] = value;
    }

    const decoratedEvent = {
      ...event,
      created: Date.now(),
      user_properties: {
        ...this.predefinedValues.user_properties, // have to be first line!
        ...event.user_properties,
      },
      event_properties: {
        ...this.predefinedValues.event_properties, // have to be first line!
        ...event.event_properties,
        ...enrichRecord,
      },
    };

    return decoratedEvent;
  }

  private static sendViaHttp(event: PlatformEvent): void {
    sendAnalyticsEvent(event);
  }

  private static sendViaWebSocket(event: PlatformEvent, type: MessageType): void {
    this.ws.sendMessage(type, MessageAction.EXECUTE, event);
  }

  private static shouldSendStartapp(
    decodedStartAppRaw: string | object | undefined
  ): boolean {
    if (!decodedStartAppRaw) return false;
    const isDecodedStartAppRawString = typeof decodedStartAppRaw === 'string';
    return isDecodedStartAppRawString;
  }
}
